import React, { useEffect, useRef, useState } from 'react';
import {
  CalendarDays,
  Check,
  ChevronRight,
  Clipboard,
  MapPin,
  Navigation,
  Package,
  Phone,
  UserRound,
  X,
} from 'lucide-react';
import {
  deliveryLocationPresentation,
  deliveryStatusPresentation,
  formatDeliveryAmount,
  formatDeliverySchedule,
} from './deliveryContextUtils';

const LOCATION_CLASSES = {
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  amber: 'border-amber-200 bg-amber-50 text-amber-700',
  red: 'border-red-200 bg-red-50 text-red-700',
};

function StatusBadge({ status }) {
  const presentation = deliveryStatusPresentation(status);
  return (
    <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-bold ${presentation.classes}`}>
      {presentation.label}
    </span>
  );
}

function LocationBadge({ delivery, short = false }) {
  const presentation = deliveryLocationPresentation(delivery);
  return (
    <span className={`inline-flex min-w-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${LOCATION_CLASSES[presentation.tone]}`}>
      <MapPin className="h-3 w-3 shrink-0" />
      <span className="truncate">{short ? presentation.shortLabel : presentation.label}</span>
    </span>
  );
}

function Detail({ label, value, wide = false, icon = null }) {
  if (value == null || value === '') return null;
  return (
    <div className={`min-w-0 rounded-xl border border-slate-200 bg-slate-50 p-3 ${wide ? 'col-span-2' : ''}`}>
      <dt className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
        {icon}
        {label}
      </dt>
      <dd className="mt-1 break-words text-sm font-semibold text-slate-900">{value}</dd>
    </div>
  );
}

export default function ConversationDeliveryContext({ delivery, onEvent }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef(null);

  const schedule = formatDeliverySchedule(delivery);
  const amount = formatDeliveryAmount(delivery?.charge);
  const driverName = delivery?.driver_name || delivery?.driver?.username;
  const trackingUrl = delivery?.tracking_url
    || (delivery?.id ? `https://www.fastmaxlima.pe/#/entrega/${delivery.id}` : null);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = event => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [open]);

  useEffect(() => () => window.clearTimeout(copiedTimerRef.current), []);

  if (!delivery || delivery.found === false) return null;

  function openDetails() {
    setOpen(true);
    onEvent?.('delivery_context_opened');
  }

  async function copyAddress() {
    if (!delivery.address || !navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(delivery.address);
      setCopied(true);
      onEvent?.('delivery_address_copied');
      window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = window.setTimeout(() => setCopied(false), 1800);
    } catch (error) {
      console.error('No se pudo copiar la direccion:', error);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={openDetails}
        className="w-full border-b border-slate-200 bg-white px-3 py-2.5 text-left transition hover:bg-slate-50 sm:px-4"
        aria-label={`Ver datos de la entrega ${delivery.id}`}
      >
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-[#B1336E]">
            <Package className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <span className="truncate text-sm font-extrabold text-slate-950">
                #{delivery.id} · {delivery.product || 'Producto sin detalle'}
              </span>
              <StatusBadge status={delivery.status} />
            </div>
            <div className="mt-1 flex min-w-0 items-center gap-2 text-xs text-slate-600">
              <span className="truncate">
                {[schedule, delivery.district].filter(Boolean).join(' · ') || 'Sin horario o distrito'}
              </span>
              <LocationBadge delivery={delivery} short />
            </div>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
        </div>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end bg-slate-950/50 sm:items-center sm:justify-center sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Datos de la entrega"
        >
          <button
            type="button"
            className="absolute inset-0 cursor-default"
            onClick={() => setOpen(false)}
            aria-label="Cerrar datos de entrega"
          />
          <div className="relative max-h-[88dvh] w-full overflow-y-auto rounded-t-2xl bg-white shadow-2xl sm:max-w-lg sm:rounded-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
              <div className="min-w-0">
                <h2 className="flex items-center gap-2 font-extrabold text-slate-950">
                  <Package className="h-5 w-5 text-[#B1336E]" />
                  Entrega #{delivery.id}
                </h2>
                <p className="truncate text-xs text-slate-500">Datos vinculados a esta conversacion</p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
                aria-label="Cerrar detalles"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3 p-4">
              <section className="rounded-xl border border-slate-200 bg-white p-3" aria-label="Resumen de entrega">
                <div className="flex min-w-0 items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-base font-extrabold text-slate-950">
                      {delivery.recipient_name || 'Cliente sin nombre'}
                    </p>
                    <p className="mt-0.5 truncate text-sm text-slate-600">
                      {delivery.product || 'Producto sin detalle'}
                    </p>
                  </div>
                  <StatusBadge status={delivery.status} />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <LocationBadge delivery={delivery} />
                  {schedule && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                      <CalendarDays className="h-3 w-3" /> {schedule}
                    </span>
                  )}
                </div>
              </section>

              <dl className="grid grid-cols-2 gap-2">
                <Detail label="Cliente" value={delivery.recipient_name} icon={<UserRound className="h-3 w-3" />} />
                <Detail label="Telefono" value={delivery.recipient_phone} icon={<Phone className="h-3 w-3" />} />
                <Detail label="Producto" value={delivery.product} wide />
                <Detail label="Fecha y horario" value={schedule} />
                <Detail label="Distrito" value={delivery.district} />
                <Detail label="Direccion" value={delivery.address} wide icon={<MapPin className="h-3 w-3" />} />
                <Detail label="Monto a cobrar" value={amount} />
                <Detail label="Motorizado" value={driverName} />
              </dl>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {trackingUrl && (
                  <a
                    href={trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => onEvent?.('delivery_tracking_opened')}
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                  >
                    <Navigation className="h-4 w-4" /> Ver seguimiento
                  </a>
                )}
                {delivery.address && (
                  <button
                    type="button"
                    onClick={copyAddress}
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Clipboard className="h-4 w-4" />}
                    {copied ? 'Direccion copiada' : 'Copiar direccion'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
